import React, { useState } from 'react';
import { Card, Icon, Button, Label, TextArea, Image, Comment, Loader, Divider } from 'semantic-ui-react';
import { formatDistanceToNow } from 'date-fns';
import {
  enUS, zhCN, es, hi, ar, pt, ru, bn, fr, de, ja, ro, it, hu, el, tr, sr
} from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { FeedComment, FeedCommentTargetType, NewsItem } from '../../app/models/Playground';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';

interface NewsCardProps { item: NewsItem; }

// ── date-fns locale helpers (same pattern as GameDetailedChat) ────────────────
const getDateLocale = (language: string) => {
  switch (language) {
    case 'en':    return enUS;
    case 'zh':    return zhCN;
    case 'es':    return es;
    case 'hi':    return hi;
    case 'ar':    return ar;
    case 'pt':    return pt;
    case 'ru':    return ru;
    case 'bn':    return bn;
    case 'fr':    return fr;
    case 'de':    return de;
    case 'ja':    return ja;
    case 'ro':    return ro;
    case 'it':    return it;
    case 'hu':    return hu;
    case 'el':    return el;
    case 'tr':    return tr;
    case 'sr':
    case 'srLatn': return sr;
    default:      return enUS;
  }
};

const convertToLatinScript = (text: string) => {
  const cyrillicToLatin: Record<string, string> = {
    'око': 'oko', 'пре': 'pre',
    'сати': 'sati', 'сат': 'sat',
    'минута': 'minuta', 'минут': 'minut',
    'секунди': 'sekundi', 'секунд': 'sekund',
    'дан': 'dan', 'дана': 'dana',
    'месец': 'mesec', 'месеца': 'meseca',
    'година': 'godina', 'године': 'godine',
  };
  let result = text;
  Object.entries(cyrillicToLatin).forEach(([cyr, lat]) => {
    result = result.replace(new RegExp(cyr, 'g'), lat);
  });
  return result;
};

export const NewsCard = observer(({ item }: NewsCardProps) => {
  const { playgroundStore, userStore } = useStore();
  const { t, i18n } = useTranslation();
  const currentUserId = userStore.user?.userID;

  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<FeedComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const handleToggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments.length === 0 && item.commentCount > 0) {
      setLoadingComments(true);
      const fetched = await playgroundStore.loadNewsComments(item.newsId);
      setComments(fetched ?? []);
      setLoadingComments(false);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    const newComment = await playgroundStore.addComment({
      content: commentText.trim(),
      targetType: FeedCommentTargetType.News,
      newsItemId: item.newsId,
    });
    if (newComment) setComments(prev => [...prev, newComment]);
    setCommentText('');
  };

  const handleDeleteComment = async (commentId: string) => {
    await playgroundStore.deleteComment(commentId);
    setComments(prev => prev.filter(c => c.id !== commentId));
  };

  // Localized time helper
  const timeAgo = (date: Date) => {
    const locale = getDateLocale(i18n.language);
    const raw = formatDistanceToNow(date, { locale });
    const time = i18n.language === 'srLatn' ? convertToLatinScript(raw) : raw;
    return t('gameDetailedChat.timeAgo', { time });
  };

  // Localized sport tag — sportTag matches sportOptions labels (e.g. "Football")
  const localizedSportTag = t(`sports.${item.sportTag.toLowerCase()}`, { defaultValue: item.sportTag });

  return (
    <Card fluid style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: 12, overflow: 'hidden', borderTop: '3px solid #00b5ad' }}>

      {item.imageUrl && (
        <Image
          src={item.imageUrl}
          alt={item.title}
          style={{ width: '100%', maxHeight: 160, objectFit: 'cover' }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}

      <Card.Content>
        {/* ── Source + time ── */}
        <Card.Meta style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Label size='mini' color='teal' style={{ borderRadius: 20 }}>
            <Icon name='tag' />
            {localizedSportTag}
          </Label>
          <span style={{ fontSize: '0.78em', color: '#555', fontWeight: 600 }}>
            <Icon name='building outline' color='grey' size='small' />
            {item.source}
          </span>
          <span style={{ fontSize: '0.75em', color: '#aaa', marginLeft: 'auto' }}>
            <Icon name='clock outline' size='small' />
            {timeAgo(item.publishedAt)}
          </span>
        </Card.Meta>

        {/* ── Title ── */}
        <Card.Header style={{ fontSize: '0.95em', lineHeight: 1.45, marginBottom: 6 }}>
          <a
            href={item.url}
            target='_blank'
            rel='noreferrer'
            style={{
              color: '#1a1a2e',
              textDecoration: 'none',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.title}
          </a>
        </Card.Header>

        {/* ── Description ── */}
        {item.description && (
          <Card.Description style={{ fontSize: '0.82em', color: '#666', lineHeight: 1.5 }}>
            {item.description.length > 140 ? item.description.slice(0, 140) + '…' : item.description}
          </Card.Description>
        )}
      </Card.Content>

      {/* ── Footer ── */}
      <Card.Content extra style={{ background: '#fafafa', paddingTop: 8, paddingBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            as='a'
            href={item.url}
            target='_blank'
            rel='noreferrer'
            color='teal'
            basic
            size='mini'
            style={{ borderRadius: 20 }}
          >
            <Icon name='external alternate' />
            {t('playground.news.readMore')}
          </Button>

          <Button
            basic
            size='mini'
            color={showComments ? 'blue' : undefined}
            onClick={handleToggleComments}
            style={{ borderRadius: 20 }}
          >
            <Icon name={showComments ? 'comment' : 'comment outline'} color={showComments ? 'blue' : 'grey'} />
            {item.commentCount > 0 && (
              <Label circular color='blue' size='mini' style={{ marginLeft: 4 }}>
                {item.commentCount}
              </Label>
            )}
            {' '}{t('playground.comment')}
          </Button>
        </div>

        {/* ── Comments section ── */}
        {showComments && (
          <div style={{ marginTop: 12 }}>
            <Divider />

            {loadingComments ? (
              <Loader active inline='centered' size='small' style={{ margin: '10px auto' }} />
            ) : comments.length > 0 ? (
              <Comment.Group minimal style={{ maxWidth: '100%', marginBottom: 10 }}>
                {comments.map(c => (
                  <Comment key={c.id}>
                    <Comment.Avatar
                      src={c.author.imageUrl || '/assets/user.png'}
                      style={{ width: 30, height: 30 }}
                    />
                    <Comment.Content style={{ background: '#f4f6fb', borderRadius: 10, padding: '6px 10px' }}>
                      <Comment.Author style={{ fontSize: '0.82em', fontWeight: 700, color: '#333' }}>
                        {c.author.fullName}
                      </Comment.Author>
                      <Comment.Metadata style={{ fontSize: '0.73em' }}>
                        <Icon name='clock outline' size='small' />
                        {timeAgo(c.createdAt)}
                      </Comment.Metadata>
                      <Comment.Text style={{ fontSize: '0.87em', marginTop: 3, color: '#333' }}>
                        {c.content}
                      </Comment.Text>
                      {c.author.userId === currentUserId && (
                        <Comment.Actions>
                          <Comment.Action
                            onClick={() => handleDeleteComment(c.id)}
                            style={{ color: '#e53e3e', fontSize: '0.76em', cursor: 'pointer' }}
                          >
                            <Icon name='trash alternate outline' size='small' />
                            {t('playground.delete')}
                          </Comment.Action>
                        </Comment.Actions>
                      )}
                    </Comment.Content>
                  </Comment>
                ))}
              </Comment.Group>
            ) : (
              <p style={{ fontSize: '0.8em', color: '#aaa', marginBottom: 8, textAlign: 'center' }}>
                <Icon name='comment outline' color='grey' />
                {t('playground.news.noComments')}
              </p>
            )}

            {/* ── New comment input ── */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <TextArea
                value={commentText}
                onChange={(_, d) => setCommentText(d.value as string)}
                rows={1}
                style={{ flex: 1, minHeight: 36, resize: 'none', fontSize: '0.88em', borderRadius: 8 }}
                placeholder={t('playground.writeComment')}
              />
              <Button
                primary
                size='small'
                disabled={!commentText.trim()}
                onClick={handleComment}
                style={{ borderRadius: 8 }}
              >
                <Icon name='send' />
                {t('playground.send')}
              </Button>
            </div>
          </div>
        )}
      </Card.Content>
    </Card>
  );
});